import {Injectable} from '@angular/core';
import {DiagramMetaService} from '../../diagram-data/diagram-meta.service';
import {RequestsService} from '../requests/requests.service';
import {DiagramJsonHandlerService} from '../../diagram-data/diagram-json-handler.service';
import {HttpParams} from '@angular/common/http';
import {TranslateService} from '@ngx-translate/core';
import {BehaviorSubject} from 'rxjs';
import {map} from 'rxjs/operators';
import {DiagramMetaStoreService} from '../../diagram-data/diagram-meta-store.service';

enum FOLLOW_LEVEL {
  NOT_FOLLOWING = -1,
  FOLLOWING = 0,
  FAVOURITE = 1
}

@Injectable({
  providedIn: 'root'
})
export class DiagramFollowService {
  followLevelSubject: BehaviorSubject<number> = new BehaviorSubject(0);
  followData$ = this.followLevelSubject.asObservable().pipe(
    map(followLevel => {
      return {
        currentIcon: this.currentIcon(followLevel),
        currentTooltip: this.currentTooltip(followLevel)
      };
    })
  );

  constructor(
    private diagramMetaSvc: DiagramMetaService,
    private requestsSvc: RequestsService,
    private diagramJsonSvc: DiagramJsonHandlerService,
    private diagramMetaStoreService: DiagramMetaStoreService,
    private translateSvc: TranslateService
  ) {}

  toggleFollow() {
    switch (this.followLevelSubject.getValue()) {
      case -1:
        this.followLevelSubject.next(0);
        this.follow();
        break;

      case 0:
        this.followLevelSubject.next(1);
        this.makeFavourite();
        break;

      case 1:
        this.followLevelSubject.next(-1);
        this.unfollow();
        break;
    }
  }

  setInitialFollow() {
    this.followLevelSubject.next(this.diagramJsonSvc.get()?.followLevel ?? -1);
  }

  follow() {
    this.requestsSvc
      .addToQueue(this.taskForFollow, true)
      .then(() => this.updateCache(FOLLOW_LEVEL.FOLLOWING));
  }

  updateCache(followLevel: number): void {
    const {diagramId, state} = this.diagramJsonSvc.get();

    this.diagramMetaStoreService.updateDiagram(diagramId, state, {followLevel});
  }

  get taskForFollow() {
    return {
      isApi: true,
      method: 'POST',
      url: '/follows',
      data: {
        modelId: this.diagramJsonSvc.get().diagramId,
        modelType: 'Diagram'
      }
    };
  }

  unfollow() {
    this.requestsSvc
      .addToQueue(this.taskForUnfollow, true)
      .then(() => this.updateCache(FOLLOW_LEVEL.NOT_FOLLOWING));
  }

  get taskForUnfollow() {
    const params = new HttpParams()
      .set('modelId', this.diagramJsonSvc.get().diagramId)
      .set('modelType', 'Diagram');

    return {
      isApi: true,
      method: 'DELETE',
      url: '/follows',
      queryParams: params
    };
  }

  makeFavourite() {
    this.requestsSvc
      .addToQueue(this.taskForMakeFavourite, true)
      .then(() => this.updateCache(FOLLOW_LEVEL.FAVOURITE));
  }

  get taskForMakeFavourite() {
    return {
      isApi: true,
      method: 'PUT',
      url: '/follows/level',
      data: {
        level: 1,
        modelId: this.diagramJsonSvc.get().diagramId,
        modelType: 'Diagram'
      }
    };
  }

  private currentIcon(followLevel) {
    switch (followLevel) {
      case -1:
        return 'follow-star-unfilled';

      case 0:
        return 'follow-star';

      case 1:
        return 'follow-star-favourite';
    }
  }

  private currentTooltip(followLevel) {
    switch (followLevel) {
      case -1:
        return this.translateSvc.instant('TOOLBAR.FLW_TT');

      case 0:
        return this.translateSvc.instant('TOOLBAR.FAV_TT');

      case 1:
        return this.translateSvc.instant('TOOLBAR.FLWING_TT');
    }
  }
}
